import { AotCategory, AotCategoryType, logConstants } from '../../utils/Constants';
import { HttpRequest, HttpResponseJson } from '../../utils/http/Http';
import accountConfigNative from '../accountConfigNative';
import { AccountType } from '../Constants';
import { NativeAccount } from '../core/TypeDefinitions';
import { Logger } from '../utils/Logger';
import { NativeBridgeBase } from './nativeBridgeBase';
import {
	GetEmailDiscoveredUrlUsingDNSandDHCPParams,
	OnWebUIReadyToLoadParams,
	PerformanceMeasureParams,
	SetAddressDiscoveredStoreUrlsParams,
	StartCloudStoreFlowParams,
	StartCustomPortalFlowParams,
	StartLegacyFlowParams,
	StartOnPremFlowParams,
	ValidateConfigSettingsParams,
	ValidateCertChainAndPayloadParams,
	WriteTraceParams,
	SetAddressDiscoveredStoreUrlsV2Params,
	AddSelectedAccountParams,
	FetchGACSSettingsV2Params,
	GACSHybridStoreInfo,
	GetAppConfigDiscoveryDocumentParams,
	GetAppConfigDiscoveryDocumentResponse,
} from './NativeInterface';
import { GACSSettingEndPoint } from '../core/AppConfigService/AppConfigService';
import { ResponseJSON } from '../../Common/BridgeInterface';

export class NativeBridge extends NativeBridgeBase {
	private static _instance: NativeBridge;
	private constructor() {
		super();
	}

	static get Instance(): NativeBridge {
		return NativeBridge._instance || (NativeBridge._instance = new NativeBridge());
	}

	async fetch(request: HttpRequest): Promise<HttpResponseJson> {
		return new Promise((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			const requestStr = request.toString();
			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native fetch with id: ${reqId} and request: ${requestStr}`,
				AotCategory.Info
			);
			accountConfigNative.fetch(reqId, requestStr);
		});
	}
	async getAccounts(): Promise<{ accounts: NativeAccount[] }> {
		return new Promise((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native getAccounts with id: ${reqId}`
			);
			accountConfigNative.getAccounts(reqId);
		});
	}

	async getEmailDiscoveredUrlUsingDNSandDHCP(
		params: GetEmailDiscoveredUrlUsingDNSandDHCPParams
	): Promise<{ discoveredUrl: string }> {
		return new Promise((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native getEmailDiscoveredUrlUsingDNSandDHCP with id: ${reqId}`
			);
			accountConfigNative.getEmailDiscoveredUrlUsingDNSandDHCP(
				reqId,
				JSON.stringify(params)
			);
		});
	}

	public writeTrace(
		module: string,
		trace: string,
		level?: string,
		aotCategory?: AotCategoryType
	): void {
		const params: WriteTraceParams = { module, level, aotCategory, message: trace };
		accountConfigNative.writeTrace(JSON.stringify(params));
	}

	accountConfigReady(): void {
		Logger.getInstance().write(logConstants.INFO, `Invoking native accountConfigReady`);
		accountConfigNative.accountConfigReady();
	}

	onWebUIReadyToLoad(webUI: string, webUIAuthentication: string): void {
		const params: OnWebUIReadyToLoadParams = { webUI, webUIAuthentication };
		const paramsStr = JSON.stringify(params);
		Logger.getInstance().write(
			logConstants.INFO,
			`Invoking native onWebUIReadyToLoad with params: ${paramsStr}`
		);
		accountConfigNative.onWebUIReadyToLoad(paramsStr);
	}

	startCloudStoreFlow(params: StartCloudStoreFlowParams): Promise<{}> {
		return new Promise((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			const paramsStr = JSON.stringify(params);
			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native startCloudStoreFlow with id: ${reqId} and params: ${paramsStr}`
			);
			accountConfigNative.startCloudStoreFlow(reqId, paramsStr);
		});
	}

	startOnPremStoreFlow(params: StartOnPremFlowParams): Promise<{}> {
		return new Promise((resolve, reject) => {
			const id = this.addRequestToQueue(resolve, reject);
			const paramsStr = JSON.stringify(params);
			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native startOnPremStoreFlow with id: ${id} and params: ${paramsStr}`
			);
			accountConfigNative.startOnPremStoreFlow(id, paramsStr);
		});
	}

	startLegacyStoreFlow(
		params: StartLegacyFlowParams
	): Promise<{ accounts: Partial<NativeAccount>[] }> {
		return new Promise((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			const paramsStr = JSON.stringify(params);

			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native startLegacyStoreFlow with id: ${reqId} and params: ${paramsStr}`
			);
			accountConfigNative.startLegacyStoreFlow(reqId, paramsStr);
		});
	}

	shouldAddAccountProceed(params: string): Promise<{ status: boolean }> {
		return new Promise((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native shouldAddAccountProceed with id: ${reqId} and params: ${params}`,
				AotCategory.Info
			);
			accountConfigNative.shouldAddAccountProceed(reqId, params);
		});
	}

	startCustomPortalFlow(params: StartCustomPortalFlowParams): Promise<{}> {
		return new Promise((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			const paramsStr = JSON.stringify(params);
			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native startCustomPortalFlow with id: ${reqId} and url: ${paramsStr}`
			);
			accountConfigNative.startCustomPortalFlow(reqId, paramsStr);
		});
	}

	addSelectedAccount(jsonParams: AddSelectedAccountParams): Promise<{}> {
		const jsonStr = JSON.stringify(jsonParams);
		return new Promise((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native addSelectedAccount with id: ${reqId} and params: ${jsonStr}`
			);
			accountConfigNative.addSelectedAccount(reqId, jsonStr);
		});
	}

	onRefreshComplete(id: string, response: ResponseJSON): void {
		const responseStr = JSON.stringify(response);
		Logger.getInstance().write(
			logConstants.INFO,
			`Invoking native onRefreshComplete with id: ${id} and response: ${responseStr}`
		);
		accountConfigNative.onRefreshComplete(id, responseStr);
	}

	showAccounts(): void {
		Logger.getInstance().write(logConstants.INFO, `Invoking native showAccounts`);
		accountConfigNative.showAccounts();
	}

	sendAction(storeID: string): void {
		Logger.getInstance().write(
			logConstants.INFO,
			`Invoking native sendAction with storeID: ${storeID}`
		);
		accountConfigNative.sendAction(storeID);
	}

	doTryAgain(): void {
		Logger.getInstance().write(logConstants.INFO, `Invoking native doTryAgain`);
		accountConfigNative.doTryAgain();
	}

	closeAddAccountView(): void {
		Logger.getInstance().write(logConstants.INFO, `Invoking native closeAddAccountView`);
		accountConfigNative.closeAddAccountView();
	}

	setAddressDiscoveredStoreUrls(params: SetAddressDiscoveredStoreUrlsParams): void {
		const paramsStr = JSON.stringify(params);
		Logger.getInstance().write(
			logConstants.INFO,
			`Invoking native setAddressDiscoveredStoreUrls with params: ${paramsStr}`
		);
		accountConfigNative.setAddressDiscoveredStoreUrls(paramsStr);
	}

	setAddressDiscoveredStoreUrlsV2(params: SetAddressDiscoveredStoreUrlsV2Params): void {
		const paramsStr = JSON.stringify(params);
		Logger.getInstance().write(
			logConstants.INFO,
			`Invoking native setAddressDiscoveredStoreUrlsV2 with params: ${paramsStr}`
		);
		accountConfigNative.setAddressDiscoveredStoreUrlsV2(paramsStr);
	}

	fetchGACSSettingsV2(params: FetchGACSSettingsV2Params): void {
		const paramsStr = JSON.stringify(params);
		Logger.getInstance().write(
			logConstants.INFO,
			`Invoking native fetchGACSSettingsV2 with params: ${paramsStr}`
		);
		accountConfigNative.fetchGACSSettingsV2(paramsStr);
	}

	openLink(params: string): void {
		Logger.getInstance().write(
			logConstants.INFO,
			`Invoking native openLink with params: ${params}`
		);
		accountConfigNative.openLink(params);
	}

	onAddAccountWithoutUIComplete(id: string, response: ResponseJSON): void {
		const responseStr = JSON.stringify(response);
		Logger.getInstance().write(
			logConstants.INFO,
			`Invoking native onAddAccountWithoutUIComplete with id: ${id} and response: ${responseStr}`
		);
		accountConfigNative.onAddAccountWithoutUIComplete(id, responseStr);
	}

	continueMigration(): void {
		Logger.getInstance().write(logConstants.INFO, `Invoking native continueMigration`);
		accountConfigNative.continueMigration();
	}

	skipMigration(): void {
		Logger.getInstance().write(logConstants.INFO, `Invoking native skipMigration`);
		accountConfigNative.skipMigration();
	}

	validateConfigSettings(
		type: AccountType,
		address: string,
		storeUrls: string[],
		gacsSettingsEndpoints: Record<string, GACSSettingEndPoint>
	): Promise<{ selectedStoreUrl: string }> {
		return new Promise((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			const params: ValidateConfigSettingsParams = {
				address,
				type,
				storeUrls,
				gacsSettingsEndpoints,
			};
			const paramsStr = JSON.stringify(params);
			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native validateConfigSettings with id: ${reqId} and params: ${paramsStr}`
			);
			accountConfigNative.validateConfigSettings(reqId, paramsStr);
		});
	}

	validatePublicCertChain(certificate: string): Promise<{}> {
		return new Promise((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native validatePublicCertchain with id: ${reqId}`
			);
			accountConfigNative.validatePublicCertChain(reqId, certificate);
		});
	}

	validateCertChainAndPayload(
		params: ValidateCertChainAndPayloadParams
	): Promise<{ isValid: boolean }> {
		return new Promise((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native validateCertChainAndPayload with id: ${reqId}`
			);
			accountConfigNative.validateCertChainAndPayload(reqId, JSON.stringify(params));
		});
	}

	getAppConfigDiscoveryDocument(
		jsonParams: GetAppConfigDiscoveryDocumentParams
	): Promise<GetAppConfigDiscoveryDocumentResponse> {
		return new Promise((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native getAppConfigDiscoveryDocument with id: ${reqId}`
			);
			accountConfigNative.getAppConfigDiscoveryDocument(
				reqId,
				JSON.stringify(jsonParams)
			);
		});
	}

	performanceMeasure(eventName: string, perfTimeStamp: string): void {
		const params: PerformanceMeasureParams = { eventName, perfTimeStamp };
		const paramsStr = JSON.stringify(params);
		Logger.getInstance().write(
			logConstants.INFO,
			`Invoking native performanceMeasure with params: ${paramsStr}`
		);
		accountConfigNative.performanceMeasure(paramsStr);
	}

	onGetCapabilitiesComplete(id: string, response: ResponseJSON): void {
		const responseStr = JSON.stringify(response);
		Logger.getInstance().write(
			logConstants.INFO,
			`Invoking native onGetCapabilitiesComplete with id: ${id} and response: ${responseStr}`
		);
		accountConfigNative.onGetCapabilitiesComplete(id, responseStr);
	}

	displayCertificate(params: string): void {
		Logger.getInstance().write(
			logConstants.INFO,
			`Invoking native displayCertificate with params: ${params}`
		);
		accountConfigNative.displayCertificate(params);
	}

	switchNativeWebView(params: string): void {
		Logger.getInstance().write(
			logConstants.INFO,
			`Invoking native switchNativeWebView with params: ${params}`
		);
		accountConfigNative.switchNativeWebView(params);
	}

	isSwitchViewAllowedOnAuthUI(): Promise<{ status: boolean }> {
		return new Promise((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native isSwitchViewAllowedOnAuthUI with id: ${reqId}`
			);
			accountConfigNative.isSwitchViewAllowedOnAuthUI(reqId);
		});
	}

	onPerformGACSDiscoveryComplete(callbackId: string, jsonResponse: ResponseJSON): void {
		const jsonStr = JSON.stringify(jsonResponse);
		Logger.getInstance().write(
			logConstants.INFO,
			`Invoking native onPerformGACSDiscoveryComplete with id: ${callbackId} and response: ${jsonStr}`
		);
		accountConfigNative.onPerformGACSDiscoveryComplete(callbackId, jsonStr);
	}

	getAllGACSHybridStoresInfo(): Promise<{ stores: GACSHybridStoreInfo[] }> {
		return new Promise((resolve, reject) => {
			const reqId = this.addRequestToQueue(resolve, reject);
			Logger.getInstance().write(
				logConstants.INFO,
				`Invoking native getAllGACSHybridStoresInfo with id: ${reqId}`
			);
			accountConfigNative.getAllGACSHybridStoresInfo(reqId);
		});
	}

	onGetNetworkLocationFromURLComplete(
		callbackId: string,
		jsonResponse: ResponseJSON
	): void {
		const jsonStr = JSON.stringify(jsonResponse);
		Logger.getInstance().write(
			logConstants.INFO,
			`Invoking native onGetNetworkLocationFromURLComplete with id: ${callbackId} and response: ${jsonStr}`
		);
		accountConfigNative.onGetNetworkLocationFromURLComplete(callbackId, jsonStr);
	}
}
